import { Linkedin, Mail } from "lucide-react";
import Reveal from "./Reveal.jsx";
import { IMAGES } from "../theme.js";
import { unsplashUrl, unsplashSrcSet } from "../lib/img.js";

const TEAM = [
  {
    name: "Erik Wetter",
    role: "Grundare · Designlead",
    bio: "Tidigare designchef i Stockholm. Driver Wetterdesign med fokus på hantverk och varumärke.",
    image: IMAGES.team.one,
  },
  {
    name: "Sara Lindqvist",
    role: "Utvecklare · AI-strateg",
    bio: "Kodar i React och vävar in AI där det skapar värde — utan att tappa det mänskliga.",
    image: IMAGES.team.two,
  },
];

const STATS = [
  { value: "40+", label: "Levererade projekt" },
  { value: "98", label: "Lighthouse-snitt" },
  { value: "3×", label: "Snabbare leverans" },
  { value: "5,0", label: "Snittbetyg" },
];

export default function About() {
  return (
    <section id="om-oss" className="relative bg-navy-950 py-24 text-white sm:py-32">
      {/* subtle texture */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            "radial-gradient(60% 80% at 80% 0%, rgba(218,184,132,0.4), transparent 60%), radial-gradient(40% 60% at 0% 100%, rgba(66,103,154,0.4), transparent 60%)",
        }}
      />
      <div className="container-x relative">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <span className="eyebrow !border-white/15 !bg-white/5 !text-sand-200">
                <span className="h-1.5 w-1.5 rounded-full bg-sand-300" />
                Om oss
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-display text-4xl font-semibold leading-tight tracking-tight text-white sm:text-5xl">
                En liten byrå med <span className="text-sand-300">stora ambitioner.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-5 text-lg leading-relaxed text-white/80">
                Vi sitter vid Vätterns strand och bygger digitala upplevelser
                åt företag i hela Sverige. Genom att kombinera erfaren design
                med moderna AI-verktyg kan vi leverera mer värde, snabbare —
                och med ett tydligt mänskligt avtryck.
              </p>
            </Reveal>

            <div className="mt-10 grid grid-cols-2 gap-6 sm:grid-cols-4">
              {STATS.map((s, i) => (
                <Reveal key={s.label} delay={0.05 * i}>
                  <div>
                    <p className="font-display text-3xl font-semibold text-sand-200">
                      {s.value}
                    </p>
                    <p className="mt-1 text-xs uppercase tracking-[0.18em] text-white/80">
                      {s.label}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:col-span-7">
            {TEAM.map((t, i) => (
              <Reveal key={t.name} delay={0.05 * i}>
                <article className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-2 backdrop-blur">
                  <div className="overflow-hidden rounded-2xl">
                    <img
                      src={unsplashUrl(t.image, { w: 600, q: 65 })}
                      srcSet={unsplashSrcSet(t.image, [400, 600, 900], 65)}
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 320px"
                      alt={t.name}
                      width="600"
                      height="750"
                      loading="lazy"
                      decoding="async"
                      className="aspect-[4/5] w-full object-cover transition duration-700 group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="px-4 pb-5 pt-4">
                    <h3 className="font-display text-2xl font-semibold text-white">
                      {t.name}
                    </h3>
                    <p className="mt-0.5 text-sm font-medium text-sand-300">
                      {t.role}
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-white/75">
                      {t.bio}
                    </p>
                    <div className="mt-4 flex items-center gap-2">
                      <a
                        href="#"
                        className="grid h-9 w-9 place-items-center rounded-full border border-white/15 bg-white/5 text-white/80 transition hover:bg-white/10 hover:text-white"
                        aria-label={`LinkedIn — ${t.name}`}
                      >
                        <Linkedin className="h-4 w-4" />
                      </a>
                      <a
                        href="#kontakt"
                        className="grid h-9 w-9 place-items-center rounded-full border border-white/15 bg-white/5 text-white/80 transition hover:bg-white/10 hover:text-white"
                        aria-label={`Maila ${t.name}`}
                      >
                        <Mail className="h-4 w-4" />
                      </a>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
